import axios from 'axios';

const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

// ─── Token & identity helpers ─────────────────────────────────────────────────
export const getToken   = ()   => localStorage.getItem('cs_token');
export const setToken   = (t)  => localStorage.setItem('cs_token', t);
export const getMongoId = ()   => localStorage.getItem('cs_mongo_id');
export const setMongoId = (id) => localStorage.setItem('cs_mongo_id', id);
export const clearToken = ()   => {
  localStorage.removeItem('cs_token');
  localStorage.removeItem('cs_mongo_id');
};

// ─── Axios instance ───────────────────────────────────────────────────────────
const api = axios.create({
  baseURL: BASE_URL,
  headers: { 'Content-Type': 'application/json' },
  timeout: 10000,
});

// Attach JWT to every outgoing request
api.interceptors.request.use((config) => {
  const token = getToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Unwrap response data and normalise error shape
api.interceptors.response.use(
  (res) => res.data,
  (err) => {
    const message =
      err.response?.data?.error ||
      err.response?.data?.message ||
      err.message ||
      'Request failed';

    // Auto-clear token if backend reports 401 (expired / invalid JWT)
    if (err.response?.status === 401) clearToken();

    return Promise.reject(new Error(message));
  }
);

// ─── Auth ─────────────────────────────────────────────────────────────────────
export const authAPI = {
  // Exchange Firebase ID token → ClubSwap JWT + MongoDB user record
  loginWithFirebase: (idToken) =>
    api.post('/api/auth/firebase', { idToken }),

  register: (username, email, password) =>
    api.post('/api/auth/register', { username, email, password }),

  login: (email, password) =>
    api.post('/api/auth/login', { email, password }),

  me: () => api.get('/api/auth/me'),

  linkTelegram: (telegramId) =>
    api.post('/api/auth/link-telegram', { telegramId }),
};

// ─── Wallet ───────────────────────────────────────────────────────────────────
export const walletAPI = {
  balance: () => api.get('/api/wallet/balance'),

  history: (limit = 20, skip = 0) =>
    api.get('/api/wallet/history', { params: { limit, skip } }),

  withdraw: (amount, cryptoAddress) =>
    api.post('/api/wallet/withdraw', { amount, cryptoAddress }),
};

// ─── Games (H2H challenges) ───────────────────────────────────────────────────
export const gamesAPI = {
  // Creates match in MongoDB and escrows challenger's stake immediately
  createChallenge: (game, stake) =>
    api.post('/api/games/challenge', { game, stake }),

  // Escrows opponent's stake; both stakes now held in escrow
  acceptChallenge: (matchId) =>
    api.post(`/api/games/${matchId}/accept`),

  // Refunds challenger's stake; cancels the match
  declineChallenge: (matchId) =>
    api.post(`/api/games/${matchId}/decline`),

  // winnerId = MongoDB _id of the winning player
  settleMatch: (matchId, winnerId) =>
    api.post(`/api/games/${matchId}/settle`, { winnerId }),

  activeMatch: () => api.get('/api/games/active'),

  history: (limit = 20) =>
    api.get('/api/games/history', { params: { limit } }),

  h2h: (opponentId) => api.get(`/api/games/h2h/${opponentId}`),
};

// ─── Tournaments ──────────────────────────────────────────────────────────────
// All list/mine calls return { tournaments: [...], total, page, limit }
export const tournamentsAPI = {
  list: (status, game, limit = 20, skip = 0) =>
    api.get('/api/tournaments', { params: { ...(status && { status }), limit, skip, ...(game && { game }) } }),

  mine: (limit = 20, skip = 0) =>
    api.get('/api/tournaments/mine', { params: { limit, skip } }),

  get: (id) => api.get(`/api/tournaments/${id}`),

  create: (data) => api.post('/api/tournaments', data),

  join: (id) => api.post(`/api/tournaments/${id}/join`),

  dispute: (id, reason) =>
    api.post(`/api/tournaments/${id}/dispute`, { reason }),
};

// ─── Disputes ─────────────────────────────────────────────────────────────────
export const disputesAPI = {
  raise: (matchId, reason, evidence = []) =>
    api.post('/api/disputes', { matchId, reason, evidence }),
};

// ─── Notifications ────────────────────────────────────────────────────────────
export const notifyAPI = {
  // Best-effort — errors are silently swallowed
  send: (mongoUserId, message) => {
    if (!mongoUserId) return Promise.resolve();
    return api.post('/api/notify', { mongoUserId, message }).catch(() => {});
  },
};
